/**
 * 直近の失敗を溜めて、画面（診断パネル）から取り出せるようにする小さなストア。
 *
 * ## 何のためにあるか
 *
 * STG（ステージング）で検証している非エンジニアが、失敗の手がかりを自分で取得できるようにする。
 * 2026-09の「STGでログインできないが原因が誰にも分からない」事象では、
 * フロントもサーバも何も記録していなかったため、原因の候補を絞ることすらできなかった。
 * エラーコードとTrace IDが1つでも手元にあれば、サーバログ・Sentryと突き合わせられる。
 *
 * ## 既定は無効（本番では何もしない）
 *
 * このストアは環境変数を読まない。`modules` はcompany / admin / customer / ai-colorから
 * 共有されており、各アプリの `config.ts` を参照させると依存が逆流する。
 * 有効化は各アプリ側で `setDiagnosticsEnabled(env === 'stg' || env === 'development')` を呼ぶ。
 * 既定が無効なので、呼ばないアプリ・本番ビルドでは `recordDiagnostic` が即returnする。
 *
 * ## 記録してよい値
 *
 * `parseStructuredError` を通した後の値（コード・表示用メッセージ・Trace ID）だけを持つ。
 * 生の `rawMessage` とスタックトレースは記録しない。既存仕様
 * （openspec/specs/error-message-hiding: サーバー内部系codeのrawMessageは表示しない）を破らないため、
 * およびminifyされたバンドルのスタックは人が読める情報にならないため。
 */

/** 保持する件数の上限。古いものから捨てる */
const MAX_ENTRIES = 30

/** 失敗の種類 */
export type DiagnosticKind =
  /** ConnectRPCの呼び出しが失敗した */
  | 'rpc'
  /** 認証基盤（Firebase）またはサインイン直後のスタッフ情報取得が失敗した */
  | 'auth'
  /** RPC以外のアプリ内の例外 */
  | 'client'

export interface DiagnosticEntry {
  /** 一覧のkey用。記録ごとに一意 */
  id: string
  /** 記録した時刻（`HH:mm:ss` のローカル時刻）。同じ失敗がまとまった場合は最新の時刻 */
  at: string
  /**
   * 同じ失敗が連続した回数。1なら1回だけ。
   * react-queryの再試行（既定3回）で同じRPCの失敗が4件並ぶと、30件の枠が同じ内容で埋まって
   * 読めなくなるため、直前と同じ（種類・操作・コードが一致）なら1件にまとめて数える。
   */
  count: number
  kind: DiagnosticKind
  /** 失敗した操作の名前（RPC名 / `firebase.signIn` など） */
  label: string
  /** アプリのエラーコード、またはConnect code / Firebaseの `error.code` */
  code: string
  /** 画面に出す用のメッセージ */
  message: string
  /** Trace ID（サーバが返していれば） */
  traceId?: string
}

/** 記録するときに渡す値（id / at / countはストアが付ける） */
export type DiagnosticInput = Omit<DiagnosticEntry, 'id' | 'at' | 'count'>

let enabled = false
let entries: readonly DiagnosticEntry[] = []
let sequence = 0
const listeners = new Set<() => void>()

/**
 * 診断の記録・表示を有効にするか。既定は無効（本番で何も動かないようにするため）。
 *
 * 購読者に通知するのは、パネルがこのフラグを`useSyncExternalStore`経由で読むため。
 * renderの中で素の`isDiagnosticsEnabled()`を読む形にすると、有効化のタイミング（いまはアプリの
 * providerのモジュールスコープ）に暗黙に依存し、将来`useEffect`へ移したときに
 * 「記録はされるがパネルが出ない」形で静かに壊れる。
 */
export const setDiagnosticsEnabled = (value: boolean): void => {
  if (enabled === value) return
  enabled = value
  notify()
}

/** 診断が有効かどうか。トーストへのTrace ID表示の出し分けにも使う */
export const isDiagnosticsEnabled = (): boolean => enabled

const notify = (): void => {
  for (const listener of listeners) {
    listener()
  }
}

/** 時刻を `HH:mm:ss` で返す（日付は当日の検証しか見ないので省く） */
const formatTime = (date: Date): string =>
  [date.getHours(), date.getMinutes(), date.getSeconds()]
    .map(part => String(part).padStart(2, '0'))
    .join(':')

/**
 * 失敗を1件記録する。診断が無効なら何もしない。
 *
 * 呼び出し側でtry/catchしなくてよいように、この関数自体は例外を投げない。
 */
export const recordDiagnostic = (input: DiagnosticInput): void => {
  if (!enabled) return
  const at = formatTime(new Date())
  const newest = entries[0]

  // 直前と同じ失敗ならまとめて数える（react-queryの再試行で同じ内容が並ぶのを防ぐ）
  if (
    newest !== undefined &&
    newest.kind === input.kind &&
    newest.label === input.label &&
    newest.code === input.code
  ) {
    entries = [{ ...newest, at, count: newest.count + 1 }, ...entries.slice(1)]
    notify()
    return
  }

  sequence += 1
  const entry: DiagnosticEntry = {
    ...input,
    id: `${String(sequence)}`,
    at,
    count: 1,
  }
  // 新しいものを先頭に置く（画面で上から読める）
  entries = [entry, ...entries].slice(0, MAX_ENTRIES)
  notify()
}

/** 記録の一覧。件数が変わらない間は同じ参照を返す（useSyncExternalStoreの要件） */
export const getDiagnostics = (): readonly DiagnosticEntry[] => entries

/** 記録を消す */
export const clearDiagnostics = (): void => {
  if (entries.length === 0) return
  entries = []
  notify()
}

/** 記録の変化を購読する。返り値を呼ぶと解除できる */
export const subscribeDiagnostics = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * テスト用: 有効フラグ・記録・連番を初期状態に戻す。
 * 購読者（listeners）は解除しない。マウント中のパネルへの通知が止まると、
 * 「リセット後の記録が画面に出ない」というテスト側の事故になるため。
 */
export const resetDiagnosticsForTest = (): void => {
  enabled = false
  entries = []
  sequence = 0
  notify()
}

/**
 * 繰り返し回数の表記。画面とコピー文面で同じ関数を使い、書式がずれないようにする。
 * 1回のときは何も出さない。
 */
export const formatRepeatCount = (count: number): string => (count > 1 ? `x${String(count)}` : '')

/**
 * 一覧を1つのテキストにする。そのままSlackに貼れる形にしたいので、
 * 記号での装飾はせず、時刻・種類・操作・エラーコード・回数・Trace IDを1行1件で並べる。
 *
 * **メッセージ（`message`）は意図的に含めない。** 表示用メッセージは
 * `parseStructuredError` の戻り値で、`google.rpc.ErrorInfo` を持たず、かつサーバー内部系の
 * Connect codeでもないエラーではサーバの`rawMessage`そのものになる。さらに重複系のコード
 * （`CUSTOMER_DUPLICATE_EMAIL` / `CUSTOMER_DUPLICATE_PHONE` / `DEAL_DUPLICATE_EXTERNAL_ID`）は
 * `formatErrorMessage` がmetadataの値を文言に埋めるため、**顧客のメールアドレスや電話番号が
 * 入りうる**。コピーされたテキストはSlackに貼られるので、そこにPIIを載せない
 * （`doc/coding_rules/no_pii_in_logs.md`）。
 *
 * 原因の切り分けに効くのはエラーコードとTrace IDと操作名で、それはここに残る。
 * メッセージは画面のパネルには出す（操作した本人が状況を読むためで、共有はされない）。
 */
export const formatDiagnosticsForCopy = (list: readonly DiagnosticEntry[]): string =>
  list
    .map(entry => {
      const parts = [entry.at, entry.kind, entry.label, entry.code]
      const repeat = formatRepeatCount(entry.count)
      if (repeat !== '') {
        parts.push(repeat)
      }
      if (entry.traceId !== undefined && entry.traceId !== '') {
        parts.push(`trace=${entry.traceId}`)
      }
      return parts.join(' | ')
    })
    .join('\n')
